import { T } from 'gt-react';
import { SpinnerIcon } from '@phosphor-icons/react';
import { useNeedInsights } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { NeedInsightsView } from './NeedInsightsView';

interface Props {
  suggestionId: string;
}

// Left-panel decision-support data: popularity, per-warehouse inventory, and
// 12-month sales history at the destination. Powers the composite-builder
// modal alongside the existing options + override on the right. Fetches by
// suggestion; the presentational render lives in NeedInsightsView (shared with
// the product-keyed ProductInsightsPanel).
export function NeedInsightsPanel({ suggestionId }: Props) {
  const { data, isError } = useNeedInsights({ suggestionId });

  // Only a genuine fetch error is an error. "No data yet" covers the pending /
  // idle-before-fetch window (where `isLoading` is briefly false) — show the
  // spinner there, not the error, so it doesn't flash "Failed to load" before
  // the insights populate.
  if (isError) {
    return (
      <div className="p-3 text-sm text-red-600" role="alert">
        <T context="error loading need insights">Failed to load insights.</T>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex h-full items-center justify-center">
        <SpinnerIcon className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <NeedInsightsView
      popularityCode={data.popularityCode}
      inventoryByWarehouse={data.inventoryByWarehouse}
      salesHistory={data.salesHistory}
      forecast={data.forecast}
    />
  );
}
